import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Box, Chip, FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import zeplinColor from 'theme/Color';
import { TOP_STAGE_OPTIONS } from '../DealsDetails.utils';

type DealDetailsHeaderProps = {
  dealData: any;
  normalizedDealType?: string;
  currentStageDisplay: string;
  onStageChange: (newStage: string) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onClose: () => void;
};

export const DealDetailsHeader = ({
  dealData,
  normalizedDealType,
  currentStageDisplay,
  onStageChange,
  onEdit,
  onDelete,
  onClose,
}: DealDetailsHeaderProps) => {
  const createdDate = dealData?.createdDate || dealData?.createdAt;
  const formattedCreatedDate = createdDate
    ? dayjs(createdDate).format('D MMM, YYYY [at] h:mm A')
    : '';

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '32px',
          flexShrink: 0,
        }}
      >
        <Typography variant='heading2'>Deal Details</Typography>

        <Box sx={{ display: 'flex', gap: { xs: '10px', xl: '16px' }, alignItems: 'center' }}>
          <FormControl size='small' sx={{ minWidth: 120 }}>
            <Select
              value={currentStageDisplay}
              onChange={(e) => onStageChange(e.target.value)}
              renderValue={(selected) => (
                <Typography
                  component='span'
                  sx={{
                    color: zeplinColor.BasicBlack,
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                  }}
                >
                  {selected}
                </Typography>
              )}
              sx={{
                borderRadius: '50px',
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: zeplinColor.Neutral5,
                fontSize: '0.875rem',
                height: '32px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  padding: '6px 32px 6px 16px',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiSelect-icon': {
                  color: zeplinColor.BasicBlack,
                  right: '12px',
                },
                '&.Mui-focused': {
                  backgroundColor: 'white',
                },
              }}
              IconComponent={KeyboardArrowDown}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '8px',
                    marginTop: '4px',
                  },
                },
              }}
            >
              {TOP_STAGE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option} sx={{ fontWeight: 'normal' }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <img
            src={'/assets/edit-thin.svg'}
            alt='edit'
            style={{ cursor: 'pointer', width: '20px', height: '20px' }}
            onClick={onEdit}
          />
          <img
            src={'/assets/delete-red.png'}
            alt='delete'
            style={{ cursor: 'pointer', width: '20px', height: '20px' }}
            onClick={onDelete}
          />
          <img
            src={'/assets/close-icon.svg'}
            alt='close'
            style={{ cursor: 'pointer', width: '20px', height: '20px' }}
            onClick={onClose}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Stack direction='row' spacing={1} sx={{ mb: 1 }} alignItems='center' flexWrap='wrap'>
          <Chip
            label='Huddle'
            size='small'
            sx={{ bgcolor: zeplinColor.Brand2, fontWeight: 'bold', height: 20 }}
          />
          {normalizedDealType && (
            <Chip
              label={normalizedDealType.replace('_', '-')}
              size='small'
              sx={{ bgcolor: zeplinColor.BasicBlack, color: zeplinColor.Neutral1, height: 20 }}
            />
          )}
          {formattedCreatedDate && (
            <Typography variant='caption' color='text.secondary'>
              Created {formattedCreatedDate}
            </Typography>
          )}
        </Stack>
        <Typography variant='h5' fontWeight='800'>
          {dealData.name || dealData.title || 'Untitled Deal'}
        </Typography>
      </Box>
    </>
  );
};
